// The Watchlist grid's look, on the same Tailwind tokens as the table kit
// (tailwind.config.js `ui` / `tbl` colours, the theme variables that
// applyThemeVars writes).
//
// - watchlistGridClasses: stable marker class names. Code (autosizeColumn,
//   WatchlistGrid's DOM lookups), tests and watchlistGrid.css find the
//   grid's parts by them; they carry no style of their own.
// - watchlistGridClassNames: each part's static Tailwind classes, joined
//   with its marker by the grid components.
// - watchlistGrid.css: the rules that depend on several states at once:
//   row striping, hover and the Watchlist row states with the pinned cells
//   that follow them, the editable-attribute tint, the spreadsheet selection
//   drawn on pseudo-elements, the header controls shown on hover, the column
//   separator, the autosize measuring mode, and the WatchlistTable column
//   group edge and live-price flash.

export const watchlistGridClasses = Object.freeze({
    root: 'watchlist-grid',
    autosizing: 'watchlist-grid--autosizing',
    spreadsheet: 'watchlist-grid--spreadsheet',
    scroller: 'watchlist-grid__scroller',
    content: 'watchlist-grid__content',
    header: 'watchlist-grid__header',
    headerRow: 'watchlist-grid__header-row',
    headerCell: 'watchlist-grid__header-cell',
    headerCellPinnedLeft: 'watchlist-grid__header-cell--pinned-left',
    headerCellWithRightBorder: 'watchlist-grid__header-cell--with-right-border',
    headerCellSortable: 'watchlist-grid__header-cell--sortable',
    headerCellSorted: 'watchlist-grid__header-cell--sorted',
    headerCellLast: 'watchlist-grid__header-cell--last',
    headerCellAlignLeft: 'watchlist-grid__header-cell--align-left',
    headerCellAlignCenter: 'watchlist-grid__header-cell--align-center',
    headerCellAlignRight: 'watchlist-grid__header-cell--align-right',
    headerContent: 'watchlist-grid__header-content',
    headerTitleContainer: 'watchlist-grid__header-title-container',
    headerTitleContent: 'watchlist-grid__header-title-content',
    headerTitle: 'watchlist-grid__header-title',
    headerIconContainer: 'watchlist-grid__header-icon-container',
    sortIcon: 'watchlist-grid__sort-icon',
    headerMenu: 'watchlist-grid__header-menu',
    headerMenuOpen: 'watchlist-grid__header-menu--open',
    columnSeparator: 'watchlist-grid__column-separator',
    columnSeparatorResizable: 'watchlist-grid__column-separator--resizable',
    columnSeparatorResizing: 'watchlist-grid__column-separator--resizing',
    columnSeparatorIcon: 'watchlist-grid__column-separator-icon',
    filler: 'watchlist-grid__filler',
    body: 'watchlist-grid__body',
    // The rows after the table's rows: pending additions and the add row.
    trailing: 'watchlist-grid__trailing',
    row: 'watchlist-grid__row',
    // Every other row, for striping.
    rowOdd: 'odd-row',
    rowFirst: 'watchlist-grid__row--first',
    rowSkeleton: 'watchlist-grid__row--skeleton',
    rowPending: 'watchlist-grid__row--pending',
    rowAdd: 'watchlist-grid__row--add',
    cell: 'watchlist-grid__cell',
    // A trailing row's one cell, pinned to the left edge.
    cellLead: 'watchlist-grid__cell--lead',
    cellPinnedLeft: 'watchlist-grid__cell--pinned-left',
    cellWithRightBorder: 'watchlist-grid__cell--with-right-border',
    cellAlignLeft: 'watchlist-grid__cell--align-left',
    cellAlignCenter: 'watchlist-grid__cell--align-center',
    cellAlignRight: 'watchlist-grid__cell--align-right',
    cellFiller: 'watchlist-grid__cell--filler',
    cellSkeleton: 'watchlist-grid__cell--skeleton',
    cellSelected: 'watchlist-grid__cell--selected',
    cellActive: 'watchlist-grid__cell--active',
    cellEdgeTop: 'watchlist-grid__cell--edge-top',
    cellEdgeRight: 'watchlist-grid__cell--edge-right',
    cellEdgeBottom: 'watchlist-grid__cell--edge-bottom',
    cellEdgeLeft: 'watchlist-grid__cell--edge-left',
    // A cell a fill-handle drag would write, showing the value it would get.
    cellFillPreview: 'watchlist-grid__cell--fill-preview',
    fillPreviewValue: 'watchlist-grid__fill-preview-value',
    skeleton: 'watchlist-grid__skeleton',
    overlay: 'watchlist-grid__overlay',
});

// The faint line between rows and under the header cells (MUI DataGrid's
// row border colour; see watchlistGrid.css --wl-row-border).
const ROW_BORDER = 'border-[color:var(--wl-row-border)]';
// Pinned cells stick to the left edge on an opaque background (set by
// watchlistGrid.css from the row's state) with a soft divider shadow.
const PINNED = 'sticky z-[4] [background-clip:padding-box] shadow-[2px_0_0_rgb(var(--c-border)/0.55)]';
const CELL_BOX = 'box-border overflow-hidden text-ellipsis whitespace-nowrap border-0 border-t border-solid '
    + 'border-t-[color:var(--wl-cell-border)] [-webkit-tap-highlight-color:transparent]';

export const watchlistGridClassNames = Object.freeze({
    // The frame: the kit table's border, radius and surface, its body type,
    // and one scrolling list inside.
    root: 'relative box-border flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-solid '
        + 'border-hairline bg-ui-surface text-[12.5px] font-medium leading-[1.35] tracking-normal text-ui-ink '
        + 'outline-none [overflow-anchor:none]',
    scroller: 'relative min-h-0 flex-[1_1_auto] overflow-auto',
    // Spreadsheet grids take focus themselves and pan on touch.
    scrollerSpreadsheet: 'outline-none touch-manipulation',
    // As wide as the columns (at least the viewport), holding the sticky
    // header and the rows; overlays such as the fill handle are positioned
    // in it, so they scroll with the rows.
    content: 'relative min-w-full',
    // Exactly as tall as the grid's header height (set inline), its bottom
    // line included; the header row fills the rest on the page colour that
    // shows past the last column.
    header: 'sticky top-0 z-[5] box-border flex flex-col rounded-t-[7px] border-0 border-b border-solid '
        + 'border-ui-line-strong bg-tbl-header',
    headerRow: 'flex min-h-0 flex-[1_1_0%] bg-bg-base',
    // Header cells: the kit header colour over the faint row line, 10px side
    // padding; a 1px accent outline on pointer focus (half strength while a
    // control inside has focus) and the app's 2px focus ring for keyboard
    // focus.
    headerCell: 'box-border flex shrink-0 grow-0 basis-auto items-center border-0 border-b border-solid '
        + `${ROW_BORDER} bg-tbl-header px-[10px] [-webkit-tap-highlight-color:transparent] `
        + 'focus-within:outline focus-within:outline-1 focus-within:-outline-offset-1 focus-within:outline-accent/50 '
        + 'focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-accent '
        + 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ui-focus',
    headerCellPosition: 'relative',
    headerCellPinned: PINNED,
    // The last pinned column's right edge.
    withRightBorder: 'border-r border-r-[color:var(--wl-row-border)]',
    // The first header cell follows the frame's inner corner.
    headerCellFirst: 'rounded-tl-[6px]',
    headerCellSortable: 'cursor-pointer',
    headerContent: { left: 'flex h-full w-full', center: 'flex h-full w-full', right: 'flex h-full w-full flex-row-reverse' },
    headerTitleContainer: {
        left: 'flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden whitespace-nowrap',
        center: 'flex min-w-0 flex-1 items-center justify-center gap-0.5 overflow-hidden whitespace-nowrap',
        right: 'flex min-w-0 flex-1 flex-row-reverse items-center gap-0.5 overflow-hidden whitespace-nowrap',
    },
    headerTitleContent: 'flex items-center overflow-hidden',
    // The kit header type: 11px extra-bold capitals, cut off on one line.
    headerTitle: 'overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-extrabold uppercase leading-[1.2] '
        + 'tracking-normal text-content-primary',
    headerIconContainer: 'flex',
    // The column menu button sits 5px into the padding on its outer side.
    headerMenu: {
        left: 'flex items-center -mr-[5px]',
        center: 'ml-auto flex items-center -mr-[5px]',
        right: '-ml-[5px] mr-auto flex items-center',
    },
    // Header icons: MUI's small icon size (smaller in compact mode).
    headerIcon: 'size-[18px] shrink-0 [[data-compact]_&]:size-[15.4px]',
    // The resize handle: a 10px strip over the column's right edge, holding
    // a 1px line (3px while hovered) in the faint row-line colour.
    columnSeparator: 'absolute z-[3] flex max-w-[10px] flex-col items-center justify-center overflow-hidden',
    columnSeparatorOffset: '-right-[5px]',
    columnSeparatorOffsetBordered: '-right-[5.5px]',
    columnSeparatorIcon: 'size-6 shrink-0 fill-current [[data-compact]_&]:size-[20.6px]',
    headerFiller: `flex-[1_0_auto] border-0 border-b border-solid ${ROW_BORDER}`,
    body: 'relative z-0',
    bodyRow: 'absolute left-0 top-0 w-full',
    // Trailing rows follow the virtualized body and are always rendered.
    trailing: 'relative z-0',
    row: 'flex [break-inside:avoid]',
    // Body cells: as tall as the row (--height on the row), text centred
    // on the line, the faint row line on top (none on the first row), cut off
    // with an ellipsis. Backgrounds come from watchlistGrid.css.
    cell: `${CELL_BOX} h-[var(--height)] shrink-0 grow-0 basis-auto px-[10px] leading-[calc(var(--height)-1px)]`,
    cellPosition: 'relative',
    cellPinned: PINNED,
    cellAlign: { left: 'justify-start text-left', center: 'justify-center text-center', right: 'justify-end text-right' },
    cellFiller: `${CELL_BOX} relative h-[var(--height)] flex-[1_0_auto] p-0 leading-[calc(var(--height)-1px)]`,
    cellSkeleton: 'inline-flex items-center',
    cellLead: 'flex min-w-0 items-center',
    // A pending addition is marked by a thin accent bar on its left edge
    // (the yellow tint means imputed factor data).
    cellLeadPending: "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-[3px] "
        + "before:bg-accent before:content-['']",
    overlay: 'absolute inset-x-0 bottom-0 z-[4] flex items-center justify-center bg-bg-base/[0.38]',
    skeleton: 'min-w-full [overflow:clip]',
    // The value a fill-handle drag would write, in lighter italic text until
    // the drag is released.
    fillPreviewValue: 'block overflow-hidden text-ellipsis px-1 italic text-content-secondary',
});
